import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|System Activity|Merge Detail List|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateSystemActivityMergeDetailList';
export type SPCandidateSystemActivityMergeDetailListLocNames =
	| `${LocNameStart}LabelAffiliationEmail`
	| `${LocNameStart}LabelAffiliationId`
	| `${LocNameStart}LabelAffiliationName`
	| `${LocNameStart}LabelAffiliationType`
	| `${LocNameStart}LabelOtherAffiliationName`
	| `${LocNameStart}LabelAddr1City`
	| `${LocNameStart}LabelAddr1CountryCode`
	| `${LocNameStart}LabelAddr1FaxNumber`
	| `${LocNameStart}LabelAddr1Line1`
	| `${LocNameStart}LabelAddr1Line2`
	| `${LocNameStart}LabelAddr1Line3`
	| `${LocNameStart}LabelAddr1PhoneNumber`
	| `${LocNameStart}LabelAddr1PostalCode`
	| `${LocNameStart}LabelAddr1State`
	| `${LocNameStart}LabelAddr2City`
	| `${LocNameStart}LabelAddr2CountryCode`
	| `${LocNameStart}LabelAddr2FaxNumber`
	| `${LocNameStart}LabelAddr2Line1`
	| `${LocNameStart}LabelAddr2Line2`
	| `${LocNameStart}LabelAddr2Line3`
	| `${LocNameStart}LabelAddr2PhoneNumber`
	| `${LocNameStart}LabelAddr2PostalCode`
	| `${LocNameStart}LabelAddr2State`
	| `${LocNameStart}LabelApprovedBy`
	| `${LocNameStart}LabelComments`
	| `${LocNameStart}LabelCompanyName`
	| `${LocNameStart}LabelEarliestExam`
	| `${LocNameStart}LabelEffective`
	| `${LocNameStart}LabelEmail`
	| `${LocNameStart}LabelHasPassword`
	| `${LocNameStart}LabelLastLogin`
	| `${LocNameStart}LabelLastUpdate`
	| `${LocNameStart}LabelLatestExam`
	| `${LocNameStart}LabelLocAddr1City`
	| `${LocNameStart}LabelLocAddr1Line1`
	| `${LocNameStart}LabelLocAddr1Line2`
	| `${LocNameStart}LabelLocAddr1Line3`
	| `${LocNameStart}LabelLocAddr1PostalCode`
	| `${LocNameStart}LabelLocAddr1State`
	| `${LocNameStart}LabelLocCompanyName`
	| `${LocNameStart}LabelMergedOn`
	| `${LocNameStart}LabelNameFirst`
	| `${LocNameStart}LabelNameLast`
	| `${LocNameStart}LabelPassword`
	| `${LocNameStart}LabelTotalExams`
	| `${LocNameStart}LabelUnique`
	| `${LocNameStart}LabelUnmergeComments`
	| `${LocNameStart}LabelUpdator`;

export const spCandidateSystemActivityMergeDetailListLocs: LocDefs<SPCandidateSystemActivityMergeDetailListLocNames> =
	{
		SPCandidateSystemActivityMergeDetailListLabelAffiliationEmail: {
			defaultValue: 'Affiliation Email',
			description: 'Label for: Affiliation Email.',
			displayPath: `${pathStart}Affiliation Email`,
			displayType: 'Label',
			legacyName:
				'CandidateProfile.PrimaryInformation.Affiliation/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.AffiliationEmail',
			feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAffiliationId: {
			defaultValue: 'Affiliation ID',
			description: 'Label for: Affiliation ID.',
			displayPath: `${pathStart}Affiliation ID`,
			displayType: 'Label',
			legacyName:
				'CandidateProfile.PrimaryInformation.Affiliation/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.AffiliationId',
			feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAffiliationName: {
			defaultValue: 'Affiliation Name',
			description: 'Label for: Affiliation Name.',
			displayPath: `${pathStart}Affiliation Name`,
			displayType: 'Label',
			legacyName:
				'CandidateProfile.PrimaryInformation.Affiliation/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.AffiliationName',
			feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAffiliationType: {
			defaultValue: 'Affiliation Type',
			description: 'Label for: Affiliation Type.',
			displayPath: `${pathStart}Affiliation Type`,
			displayType: 'Label',
			legacyName:
				'CandidateProfile.PrimaryInformation.Affiliation/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.AffiliationType',
			feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelOtherAffiliationName: {
			defaultValue: 'Other Affiliation Name',
			description: 'Label for: Other Affiliation Name.',
			displayPath: `${pathStart}Other Affiliation Name`,
			displayType: 'Label',
			legacyName:
				'CandidateProfile.PrimaryInformation.Affiliation/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.OtherAffiliationName',
			feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1City: {
			defaultValue: 'Address City',
			description: 'The label for the address city field',
			displayPath: `${pathStart}Address City`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1City',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1CountryCode: {
			defaultValue: 'Address Country Code',
			description: 'The label for the address country code field',
			displayPath: `${pathStart}Address Country Code`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1CountryCode',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1FaxNumber: {
			defaultValue: 'Address Fax Number',
			description: 'The label for the address fax number field',
			displayPath: `${pathStart}Address Fax Number`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1FaxNumber',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1Line1: {
			defaultValue: 'Address, Line 1',
			description: 'The label for the address, line 1 field',
			displayPath: `${pathStart}Address, Line 1`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1Line1',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1Line2: {
			defaultValue: 'Address, Line 2',
			description: 'The label for the address, line 2 field',
			displayPath: `${pathStart}Address, Line 2`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1Line2',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1Line3: {
			defaultValue: 'Address, Line 3',
			description: 'The label for the address, line 3 field',
			displayPath: `${pathStart}Address, Line 3`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1Line3',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1PhoneNumber: {
			defaultValue: 'Address Phone Number',
			description: 'The label for the address phone number field',
			displayPath: `${pathStart}Address Phone Number`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1PhoneNumber',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1PostalCode: {
			defaultValue: 'Address Zip/Postal Code',
			description: 'The label for the address zip/postal code field',
			displayPath: `${pathStart}Address Zip/Postal Code`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1PostalCode',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr1State: {
			defaultValue: 'Address State',
			description: 'The label for the address state field',
			displayPath: `${pathStart}Address State`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr1State',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2City: {
			defaultValue: 'Other Address City',
			description: 'The label for the other address city field',
			displayPath: `${pathStart}Other Address City`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2City',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2CountryCode: {
			defaultValue: 'Other Address Country Code',
			description: 'The label for the other address country code field',
			displayPath: `${pathStart}Other Address Country Code`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2CountryCode',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2FaxNumber: {
			defaultValue: 'Other Address Fax Number',
			description: 'The label for the other address fax number field',
			displayPath: `${pathStart}Other Address Fax Number`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2FaxNumber',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2Line1: {
			defaultValue: 'Other Address, Line 1',
			description: 'The label for the other address, line 1 field',
			displayPath: `${pathStart}Other Address, Line 1`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2Line1',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2Line2: {
			defaultValue: 'Other Address, Line 2',
			description: 'The label for the other address, line 2 field',
			displayPath: `${pathStart}Other Address, Line 2`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2Line2',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2Line3: {
			defaultValue: 'Other Address, Line 3',
			description: 'The label for the other address, line 3 field',
			displayPath: `${pathStart}Other Address, Line 3`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2Line3',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2PhoneNumber: {
			defaultValue: 'Other Address Phone Number',
			description: 'The label for the other address phone number field',
			displayPath: `${pathStart}Other Address Phone Number`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2PhoneNumber',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2PostalCode: {
			defaultValue: 'Other Address Zip/Postal Code',
			description: 'The label for the other address zip/postal code field',
			displayPath: `${pathStart}Other Address Zip/Postal Code`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2PostalCode',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelAddr2State: {
			defaultValue: 'Other Address State',
			description: 'The label for the other address state field',
			displayPath: `${pathStart}Other Address State`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Addr2State',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelApprovedBy: {
			defaultValue: 'Approved By',
			description: 'The label for the approved by',
			displayPath: `${pathStart}Approved By`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.ApprovedBy',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelComments: {
			defaultValue: 'Comments',
			description: 'The for the comments label',
			displayPath: `${pathStart}Comments`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Comments',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelCompanyName: {
			defaultValue: 'Company Name',
			description: 'The label for the company name field',
			displayPath: `${pathStart}Company Name`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.CompanyName',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelEarliestExam: {
			defaultValue: 'Earliest Exam',
			description: 'The label for the earliest exam field',
			displayPath: `${pathStart}Earliest Exam`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.EarliestExam',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelEffective: {
			defaultValue: 'Effective',
			description: 'The label for the effective field',
			displayPath: `${pathStart}Effective`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Effective',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelEmail: {
			defaultValue: 'Email',
			description: 'The label for the email field',
			displayPath: `${pathStart}Email`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Email',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelHasPassword: {
			defaultValue: 'Has Password',
			description: 'The label for the has password field',
			displayPath: `${pathStart}Has Password`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.HasPassword',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLastLogin: {
			defaultValue: 'Last Login',
			description: 'The label for the last login field',
			displayPath: `${pathStart}Last Login`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LastLogin',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLastUpdate: {
			defaultValue: 'Last Update',
			description: 'The label for the last update field',
			displayPath: `${pathStart}Last Update`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LastUpdate',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLatestExam: {
			defaultValue: 'Latest Exam',
			description: 'The label for the latest exam field',
			displayPath: `${pathStart}Latest Exam`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LatestExam',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLocAddr1City: {
			defaultValue: 'Localized Address City',
			description: 'The label for the localized address city field',
			displayPath: `${pathStart}Localized Address City`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LocAddr1City',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLocAddr1Line1: {
			defaultValue: 'Localized Address, Line 1',
			description: 'The label for the localized address, line 1 field',
			displayPath: `${pathStart}Localized Address, Line 1`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LocAddr1Line1',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLocAddr1Line2: {
			defaultValue: 'Localized Address, Line 2',
			description: 'The label for the localized address, line 2 field',
			displayPath: `${pathStart}Localized Address, Line 2`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LocAddr1Line2',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLocAddr1Line3: {
			defaultValue: 'Localized Address, Line 3',
			description: 'The label for the localized address, line 3 field',
			displayPath: `${pathStart}Localized Address, Line 3`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LocAddr1Line3',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLocAddr1PostalCode: {
			defaultValue: 'Localized Address Zip/Postal Code',
			description: 'The label for the localized address zip/postal code field',
			displayPath: `${pathStart}Localized Address Zip/Postal Code`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LocAddr1PostalCode',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLocAddr1State: {
			defaultValue: 'Localized Address State',
			description: 'The label for the localized address state field',
			displayPath: `${pathStart}Localized Address State`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LocAddr1State',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelLocCompanyName: {
			defaultValue: 'Localized Company Name',
			description: 'The label for the localized company name field',
			displayPath: `${pathStart}Localized Company Name`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.LocCompanyName',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelMergedOn: {
			defaultValue: 'Merged On',
			description: 'The label for the merged on',
			displayPath: `${pathStart}Merged On`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.MergedOn',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelNameFirst: {
			defaultValue: 'First Name',
			description: 'The label for the first name field',
			displayPath: `${pathStart}First Name`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.NameFirst',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelNameLast: {
			defaultValue: 'Last Name',
			description: 'The label for the last name field',
			displayPath: `${pathStart}Last Name`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.NameLast',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelPassword: {
			defaultValue: 'Password Set',
			description: 'The label for the password set field',
			displayPath: `${pathStart}Password Set`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Password',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelTotalExams: {
			defaultValue: 'Total Exams',
			description: 'The label for the total exams field',
			displayPath: `${pathStart}Total Exams`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.TotalExams',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelUnique: {
			defaultValue: 'Unique',
			description: 'The label for the unique field',
			displayPath: `${pathStart}Unique`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Unique',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelUnmergeComments: {
			defaultValue: 'Comments',
			description: 'The label for the comments on an unmerge request',
			displayPath: `${pathStart}Comments`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.UnmergeComments',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
		SPCandidateSystemActivityMergeDetailListLabelUpdator: {
			defaultValue: 'Updator',
			description: 'The label for the updator field',
			displayPath: `${pathStart}Updator`,
			displayType: 'Label',
			legacyName:
				'MergeCandidates/Candidates.SystemActivity.Merges.MergeDetail.FieldLabel.Updator',
			feature: HubFeature.MergeCandidates,
			...sharedProperties,
		},
	};

export type SPCandidateSystemActivityMergeDetailListLocType =
	LocMap<SPCandidateSystemActivityMergeDetailListLocNames>;
